import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import JustificationContainer from '~/components/atom/JustificationContainer';
import NavigationItem from './components/NavigationItem';
import { navigationConfig, SIDE_MAX_WIDTH, SIDE_MIN_WIDTH } from './constants';
import SettingsMenu from './components/SettingsMenu';
import AccountSwitcher from './components/AccountSwitcher';
import use100vh from '~/hooks/use100vh';
import useCurrentBreakpoint from '~/hooks/useCurrentBreakpoint';
import Button from '~/components/atom/Button';

export type Props = {};

const text = {
  settings: 'Instellingen',
  logout: 'Uitloggen',
};

const Navigation: React.FCC<Props> = ({ dataTestId }) => {
  const containerRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [settingsExpanded, setExpandSettings] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentBreakpoint = useCurrentBreakpoint();
  const isSmallScreen =
    currentBreakpoint === 'mobile' || currentBreakpoint === 'tablet';

  const navHeight = use100vh();

  const desktopExpanded = expanded || settingsExpanded;
  const isExpanded = isSmallScreen ? mobileOpen : desktopExpanded;

  const closeMobileNav = () => {
    if (isSmallScreen) setMobileOpen(false);
  };

  return (
    <>
      {isSmallScreen && (
        <HamburgerButton
          ghost
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label="Menu"
          icon={mobileOpen ? 'close' : 'menu'}
          size="medium"
        />
      )}
      {isSmallScreen && mobileOpen && (
        <Overlay onClick={() => setMobileOpen(false)} />
      )}
      <Container
        data-testid={dataTestId}
        $expanded={isExpanded}
        $navHeight={navHeight}
        $isSmallScreen={isSmallScreen}
        $mobileOpen={mobileOpen}
        onMouseEnter={() => {
          if (!isSmallScreen) setExpanded(true);
        }}
        onMouseLeave={() => {
          if (!isSmallScreen) setExpanded(false);
        }}
        ref={containerRef}
      >
        <JustificationContainer
          direction="column"
          justification="space-between"
          height="100%"
          width="100%"
        >
          <JustificationContainer direction="column" width="100%" gap="xxs">
            {navigationConfig.map(item => (
              <NavigationItem
                key={item.label?.toString()}
                expanded={isExpanded}
                onClick={() => {
                  setExpanded(false);
                  closeMobileNav();
                }}
                {...item}
              />
            ))}
          </JustificationContainer>
          <JustificationContainer direction="column" width="100%" gap="xxs">
            <SettingsContainer>
              <AccountSwitcher expanded={isExpanded} />
              <NavigationItem
                expanded={isExpanded}
                icon={{ name: 'gear' }}
                label={text.settings}
                onClick={() => setExpandSettings(prev => !prev)}
                dataTestId="global-settings-button"
              />
              {settingsExpanded && (
                <SettingsMenu
                  onNavigate={() => {
                    setExpanded(false);
                    setExpandSettings(false);
                    closeMobileNav();
                  }}
                />
              )}
            </SettingsContainer>
            <NavigationItem
              dataTestId="global-logout-button"
              expanded={isExpanded}
              icon={{ name: 'sign-out' }}
              to="/logout"
              label={text.logout}
            />
          </JustificationContainer>
        </JustificationContainer>
      </Container>
    </>
  );
};

const Container = styled.nav<{
  $expanded: boolean;
  $navHeight: string;
  $isSmallScreen: boolean;
  $mobileOpen: boolean;
}>(
  ({ theme, $expanded, $navHeight, $isSmallScreen, $mobileOpen }) => css`
    padding: calc(${theme.space('xxxxl')} * 2.5) ${theme.space('xxs')}
      ${theme.space('xxs')} ${theme.space('xxs')};
    width: ${$expanded ? SIDE_MAX_WIDTH : SIDE_MIN_WIDTH};
    height: ${$navHeight};
    background-color: ${theme.color('primary', 'dark')};
    color: ${theme.color('white')};
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: ${theme.z('top') - 1};
    transition:
      width 0.2s ease-out,
      transform 0.2s ease-out;

    ${$isSmallScreen &&
    css`
      overflow-y: auto;
      width: 100vw;
      transform: translateX(${$mobileOpen ? '0' : '-100%'});
      z-index: ${theme.z('top') + 1};
    `}
  `,
);

const HamburgerButton = styled(Button)(
  ({ theme }) => css`
    position: fixed;
    top: ${theme.space('s')};
    left: ${theme.space('s')};
    z-index: ${theme.z('top') + 2};
    background: ${theme.color('primary', 'dark')};
    color: ${theme.color('white')};
    border: 0.5px solid ${theme.color('white')};
    box-shadow: none;

    &:hover,
    &:active,
    &:focus {
      box-shadow: none;
      background-color: ${theme.color('primary', 'dark')} !important;
      color: ${theme.color('white')};
    }
  `,
);

const Overlay = styled.div(
  ({ theme }) => css`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: ${theme.z('top')};
  `,
);

const SettingsContainer = styled.div`
  width: 100%;
  position: relative;
`;

export default Navigation;
